import {
    CardConfig,
    CardKey,
    IBudgetClassificationPatchData,
    IProFormaUses,
    IUsesSummary,
    SaveInput,
} from 'views/ProFormaTable/types';
import { BudgetClassificationTypeEnum } from 'views/ProFormaTable/constants';
import assertNever from 'helpers/assertNever';

export const HIGH_LEVEL_DESCRIPTIONS: Record<CardKey, string> = {
    acquisition: 'Acquisition Costs',
    hard: 'Hard Costs',
    soft: 'Soft Costs',
    financing: 'Financing Costs',
    operatingReserve: 'Operating Reserve',
};

const CALCULATED_TYPES: ReadonlySet<BudgetClassificationTypeEnum> = new Set([
    BudgetClassificationTypeEnum.INTEREST_RESERVE,
    BudgetClassificationTypeEnum.OPERATING_RESERVE,
    BudgetClassificationTypeEnum.DEVELOPER_FEE,
]);

const isCalculatedLine = (useLine: IProFormaUses) => CALCULATED_TYPES.has(useLine.classificationType);

const editableLine = (useLine: IProFormaUses): IProFormaUses[] =>
    (useLine.children ?? []).filter(subcategory =>
        (subcategory.children ?? []).some(useLine => !isCalculatedLine(useLine)));

export const findHighLevelByDescription = (useLines: IProFormaUses[], description: string): IProFormaUses | undefined =>
    useLines.find(useLine => useLine.description === description);

/**
 * Determines if a use line is editable. A use line is editable if it has exactly one subcategory
 * that is not auto-calculated.
 * @param useLine - The use line to check if it is editable.
 * @returns True if the use line is editable, false otherwise.
 */
export const isCardEditable = (useLine: IProFormaUses | undefined): boolean => {
    if (!useLine) return false;

    const editableLines = editableLine(useLine);
    if (editableLines.length !== 1) return false;

    const realLines = (editableLines[0].children ?? []).filter(useLine => !isCalculatedLine(useLine));
    return realLines.length === 1;
};

export const getEditableUseLine = (useLine: IProFormaUses | undefined): IProFormaUses | undefined => {
    if (!useLine || !isCardEditable(useLine)) return undefined;

    const line = editableLine(useLine)[0];
    return line.children?.find(useLine => !isCalculatedLine(useLine));
};

const findCalculatedLineAmount = (
    highLevel: IProFormaUses | undefined,
    classificationType: BudgetClassificationTypeEnum,
): number | null | undefined => {
    const line = highLevel?.children
        ?.flatMap(subcategory => subcategory.children ?? [])
        .find(useLine => useLine.classificationType === classificationType);
    return line?.amount;
};

export const getDeveloperFeeAmount = (highLevel: IProFormaUses | undefined) =>
    findCalculatedLineAmount(highLevel, BudgetClassificationTypeEnum.DEVELOPER_FEE);

export const getInterestReserveAmount = (highLevel: IProFormaUses | undefined) =>
    findCalculatedLineAmount(highLevel, BudgetClassificationTypeEnum.INTEREST_RESERVE);

const formatPercent = (value: number) => `${value.toFixed(2)}%`;

export const getUsesCards = (uses: IUsesSummary): CardConfig[] => {
    const highLevels = uses.uses ?? [];
    const acquisition = findHighLevelByDescription(highLevels, HIGH_LEVEL_DESCRIPTIONS.acquisition);
    const hard = findHighLevelByDescription(highLevels, HIGH_LEVEL_DESCRIPTIONS.hard);
    const soft = findHighLevelByDescription(highLevels, HIGH_LEVEL_DESCRIPTIONS.soft);
    const financing = findHighLevelByDescription(highLevels, HIGH_LEVEL_DESCRIPTIONS.financing);
    const operatingReserve = findHighLevelByDescription(highLevels, HIGH_LEVEL_DESCRIPTIONS.operatingReserve);

    const editableAcquisitionLine = getEditableUseLine(acquisition);
    const editableHardLine = getEditableUseLine(hard);
    const editableSoftLine = getEditableUseLine(soft);
    const editableFinancingLine = getEditableUseLine(financing);
    const editableOperatingReserveLine = getEditableUseLine(operatingReserve);

    const softFractionOfHard = editableSoftLine?.amount != null && hard?.amount != null && Number(hard.amount) > 0
        ? editableSoftLine.amount / Number(hard.amount)
        : null;
    const softPctOfHard = softFractionOfHard != null && Number.isFinite(softFractionOfHard) ? softFractionOfHard * 100 : null;

    const financingPctOfDebt = editableFinancingLine?.percentageOfDebt;
    const financingDebtAmount = editableFinancingLine?.amount != null && financingPctOfDebt != null && financingPctOfDebt > 0
        ? editableFinancingLine.amount / financingPctOfDebt
        : undefined;

    return [
        {
            key: 'acquisition',
            highLevel: acquisition,
            editVariant: 'dollar-only',
            editableTotal: editableAcquisitionLine?.amount,
            editableCostPerRSF: editableAcquisitionLine?.costPerRentableSquareFootage,
        },
        {
            key: 'hard',
            highLevel: hard,
            editVariant: 'dollar-only',
            editableTotal: editableHardLine?.amount,
            editableCostPerRSF: editableHardLine?.costPerRentableSquareFootage,
        },
        {
            key: 'soft',
            highLevel: soft,
            supplemental: `${softPctOfHard != null ? formatPercent(softPctOfHard) : '—'} of hard costs`,
            editVariant: 'dollar-or-pct-of-hard',
            editContext: {
                hardTotal: hard?.amount ?? undefined,
                initialPercent: softFractionOfHard ?? undefined,
            },
            editableTotal: editableSoftLine?.amount,
            editableCostPerRSF: editableSoftLine?.costPerRentableSquareFootage,
            initialEditMode: 'percentage',
            calculatedSublines: [{ label: 'Developer Fee', amount: getDeveloperFeeAmount(soft) }],
        },
        {
            key: 'financing',
            highLevel: financing,
            supplemental: financingPctOfDebt != null ? `${formatPercent(financingPctOfDebt * 100)} of debt amount` : undefined,
            editVariant: 'dollar-or-pct-of-debt',
            editContext: {
                debtAmount: financingDebtAmount,
                initialPercent: financingPctOfDebt ?? undefined,
            },
            editableTotal: editableFinancingLine?.amount,
            editableCostPerRSF: editableFinancingLine?.costPerRentableSquareFootage,
            initialEditMode: 'percentage',
            calculatedSublines: [{ label: 'Interest Reserve', amount: getInterestReserveAmount(financing) }],
        },
        {
            key: 'operatingReserve',
            highLevel: operatingReserve,
            editVariant: 'dollar-only',
            editableTotal: editableOperatingReserveLine?.amount,
            editableCostPerRSF: editableOperatingReserveLine?.costPerRentableSquareFootage,
        },
    ];
};

/**
 * Maps a card's save action to the patch payload accepted by the `update_budget_classifications` socket event.
 */
export const buildSavePatch = (input: SaveInput): Partial<IBudgetClassificationPatchData> => {
    switch (input.mode) {
        case 'dollar':
            return {
                amount: String(input.value),
                calculatedOn: 'amount',
            };

        case 'soft-pct':
        {
            const dollars = input.hardTotal * (input.percent / 100);
            return {
                amount: String(dollars),
                calculatedOn: 'amount',
            };
        }

        case 'financing-pct': {
            const fraction = Math.min(Math.max(input.percent / 100, 0), 1);
            return {
                percentageOfDebt: String(fraction),
                calculatedOn: 'percentage_of_debt',
            };
        }

        case 'per-rsf':
            return {
                costPerRentableSquareFootage: String(input.value),
                calculatedOn: 'cost_per_rentable_square_footage',
            };

        default:
            return assertNever(input);
    }
};
